import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import { ImageInput } from '../components/ImagePicker';
import { productsAPI } from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  CubeIcon,
  CalendarIcon,
  LinkIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  BeakerIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

const ProductsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [iconUrl, setIconUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const queryClient = useQueryClient();

  // Utility function to normalize features format
  const normalizeFeatures = (features) => {
    if (!features) return [];
    if (typeof features === 'string') {
      try {
        return JSON.parse(features);
      } catch {
        return features.split(',').map(f => f.trim()).filter(Boolean);
      }
    }
    return Array.isArray(features) ? features : [];
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  // Fetch products based on filters
  const { data: productsData, isLoading } = useQuery(
    ['products', statusFilter, activeFilter],
    () => {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      // Always send the active parameter
      params.active = activeFilter;
      return productsAPI.getAll(params).then(res => res.data.data);
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch all products for accurate counts
  const { data: allProducts } = useQuery(
    ['products', 'all'],
    () => productsAPI.getAll({ active: 'all' }).then(res => res.data.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Create product mutation
  const createMutation = useMutation(
    (data) => productsAPI.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('Product created successfully!');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create product');
      }
    }
  );

  // Update product mutation
  const updateMutation = useMutation(
    ({ id, data }) => productsAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('Product updated successfully!');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product');
      }
    }
  );

  // Delete product mutation
  const deleteMutation = useMutation(
    (id) => productsAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('Product deleted successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete product');
      }
    }
  );

  // Toggle active status mutation
  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => productsAPI.update(id, { isActive: !isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('Product status updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product status');
      }
    }
  );

  const handleOpenModal = (product = null) => {
    setEditingProduct(product);
    if (product) {
      // Populate form with existing data
      Object.keys(product).forEach(key => {
        if (key === 'features') {
          const featuresArray = normalizeFeatures(product[key]);
          setValue(key, featuresArray.join(', '));
        } else if (key === 'releaseDate') {
          setValue(key, product[key] ? new Date(product[key]).toISOString().split('T')[0] : '');
        } else {
          setValue(key, product[key] || '');
        }
      });
      setIconUrl(product.icon || '');
      setImageUrl(product.image || '');
    } else {
      reset();
      setIconUrl('');
      setImageUrl('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    reset();
    setIconUrl('');
    setImageUrl('');
  };

  const onSubmit = (data) => {
    const productData = {
      ...data,
      icon: iconUrl,
      image: imageUrl,
      features: data.features ? data.features.split(',').map(f => f.trim()).filter(Boolean) : [],
      releaseDate: data.releaseDate || null,
      order: parseInt(data.order) || 0
    };

    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: productData });
    } else {
      createMutation.mutate(productData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleActive = (product) => {
    toggleActiveMutation.mutate({ id: product.id, isActive: product.isActive });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Coming Soon': return <ClockIcon className="h-4 w-4" />;
      case 'In Development': return <BeakerIcon className="h-4 w-4" />;
      case 'Beta': return <CheckCircleIcon className="h-4 w-4" />;
      case 'Released': return <RocketLaunchIcon className="h-4 w-4" />;
      default: return <CubeIcon className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Coming Soon': return 'bg-gray-100 text-gray-800';
      case 'In Development': return 'bg-blue-100 text-blue-800';
      case 'Beta': return 'bg-yellow-100 text-yellow-800';
      case 'Released': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Products">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Products">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Products</h1>
            <p className="text-gray-600">Manage your products and their information</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Product
          </button>
        </div>
        
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status:</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'Coming Soon', label: 'Coming Soon' },
                  { value: 'In Development', label: 'In Development' },
                  { value: 'Beta', label: 'Beta' },
                  { value: 'Released', label: 'Released' }
                ].map((filter) => {
                  const count = filter.value === 'all' ? allProducts?.length || 0 :
                               allProducts?.filter(p => p.status === filter.value).length || 0;
                  
                  return (
                    <button
                      key={filter.value}
                      onClick={() => setStatusFilter(filter.value)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === filter.value
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                      <span className="ml-1 bg-white bg-opacity-50 text-xs px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status:</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Products' },
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' }
                ].map((filter) => {
                  const count = filter.value === 'all' ? allProducts?.length || 0 :
                               filter.value === 'true' ? allProducts?.filter(p => p.isActive).length || 0 :
                               allProducts?.filter(p => !p.isActive).length || 0;
                  
                  return (
                    <button
                      key={filter.value}
                      onClick={() => setActiveFilter(filter.value)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        activeFilter === filter.value
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                      <span className="ml-1 bg-white bg-opacity-50 text-xs px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {productsData?.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Product Image */}
              <div className="h-48 bg-gray-200 relative">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <CubeIcon className="h-16 w-16 text-gray-400" />
                  </div>
                )}
                
                {/* Status Badges */}
                <div className="absolute top-2 left-2 flex gap-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}
                  >
                    {getStatusIcon(product.status)}
                    <span className="ml-1">{product.status}</span>
                  </span>
                </div>
                
                <div className="absolute top-2 right-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      product.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {product.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      {product.icon && (
                        <img src={product.icon} alt="" className="h-6 w-6 mr-2" />
                      )}
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {product.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {product.slug}
                    </p>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {product.description}
                    </p>
                  </div>
                </div>

                {/* Release Date */}
                {product.releaseDate && (
                  <div className="mt-3 flex items-center text-sm text-gray-500">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <span>Release: {new Date(product.releaseDate).toLocaleDateString()}</span>
                  </div>
                )}

                {/* Links */}
                <div className="mt-3 flex space-x-2">
                  {product.demoUrl && (
                    <a
                      href={product.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Demo
                    </a>
                  )}
                  {product.downloadUrl && (
                    <a
                      href={product.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-green-600 hover:text-green-800"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Download
                    </a>
                  )}
                </div>

                {/* Features */}
                {product.features && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-1">
                      {normalizeFeatures(product.features).slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {feature}
                        </span>
                      ))}
                      {normalizeFeatures(product.features).length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          +{normalizeFeatures(product.features).length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Order: {product.order || 0}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleActive(product)}
                      className="text-gray-400 hover:text-gray-600"
                      title={product.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {product.isActive ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleOpenModal(product)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {productsData?.length === 0 && (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new product.
            </p>
            <div className="mt-6">
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Product
              </button>
            </div>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Title *
                    </label>
                    <input
                      {...register('title', { required: 'Title is required' })}
                      type="text"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Product Title"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Slug *
                    </label>
                    <input
                      {...register('slug', { required: 'Slug is required' })}
                      type="text"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="product-slug"
                    />
                    {errors.slug && (
                      <p className="mt-1 text-sm text-red-600">{errors.slug.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Product description..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Coming Soon">Coming Soon</option>
                      <option value="In Development">In Development</option>
                      <option value="Beta">Beta</option>
                      <option value="Released">Released</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Order
                    </label>
                    <input
                      {...register('order')}
                      type="number"
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Features (comma-separated)
                  </label>
                  <input
                    {...register('features')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Feature 1, Feature 2, Feature 3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Release Date
                    </label>
                    <input
                      {...register('releaseDate')}
                      type="date"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      {...register('isActive')}
                      type="checkbox"
                      id="isActive"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Demo URL
                    </label>
                    <input
                      {...register('demoUrl')}
                      type="url"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://demo.example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Download URL
                    </label>
                    <input
                      {...register('downloadUrl')}
                      type="url"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://download.example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImageInput
                    label="Icon"
                    value={iconUrl}
                    onChange={setIconUrl}
                    placeholder="Select product icon"
                    category="icons"
                  />

                  <ImageInput
                    label="Image"
                    value={imageUrl}
                    onChange={setImageUrl}
                    placeholder="Select product image"
                    category="products"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading
                      ? 'Saving...'
                      : editingProduct
                      ? 'Update Product'
                      : 'Create Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProductsPage;